export interface DiffLine {
  kind: 'same' | 'added' | 'removed';
  text: string;
}

/** Above this many line pairs the LCS table is too costly; fall back to remove-all/add-all. */
const MAX_CELLS = 4_000_000;

/** Line diff (longest common subsequence) of two texts, in display order. */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before === '' ? [] : before.split('\n');
  const b = after === '' ? [] : after.split('\n');
  // Trim the common prefix and suffix first: typical edits touch a few lines.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const head: DiffLine[] = a.slice(0, start).map((text) => ({ kind: 'same', text }));
  const tail: DiffLine[] = a.slice(endA).map((text) => ({ kind: 'same', text }));
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);

  if (midA.length * midB.length > MAX_CELLS) {
    return [
      ...head,
      ...midA.map((text) => ({ kind: 'removed' as const, text })),
      ...midB.map((text) => ({ kind: 'added' as const, text })),
      ...tail,
    ];
  }

  const n = midA.length;
  const m = midB.length;
  // lcs[i][j] = LCS length of midA[i..] and midB[j..], flattened.
  const lcs = new Uint32Array((n + 1) * (m + 1));
  const at = (i: number, j: number) => i * (m + 1) + j;
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[at(i, j)] =
        midA[i] === midB[j]
          ? lcs[at(i + 1, j + 1)]! + 1
          : Math.max(lcs[at(i + 1, j)]!, lcs[at(i, j + 1)]!);
    }
  }
  const mid: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (midA[i] === midB[j]) {
      mid.push({ kind: 'same', text: midA[i]! });
      i++;
      j++;
    } else if (lcs[at(i + 1, j)]! >= lcs[at(i, j + 1)]!) {
      mid.push({ kind: 'removed', text: midA[i++]! });
    } else {
      mid.push({ kind: 'added', text: midB[j++]! });
    }
  }
  while (i < n) mid.push({ kind: 'removed', text: midA[i++]! });
  while (j < m) mid.push({ kind: 'added', text: midB[j++]! });
  return [...head, ...mid, ...tail];
}
